import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Card,
  EditIcon,
  LocalOfferIcon,
  MoneyIcon,
  Table,
  formatCurrency,
} from '@avila-tek/ui/src';
import { useRouter } from 'next/router';
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation,
} from '@apollo/client';
import NProgress from 'nprogress';
import { Product, Variant } from '../../models';
import { ActiveDotCell } from '../shared';
import { CREATE_VARIANT } from '../../graphql/mutation';
import { useNotify } from '../../hooks';
import VariantModal from './VariantModal/VariantModal';

interface VariantTableProps {
  variants: Variant[];
  product: Product;
  refetch?: (variables?: Partial<OperationVariables>) => Promise<
    ApolloQueryResult<{
      product: Product;
    }>
  >;
}

interface ActionCellProps {
  productId: string;
  variantId: string;
  codesAutogenerated: boolean;
}

function ActionCell({
  productId,
  variantId,
  codesAutogenerated,
}: ActionCellProps) {
  const router = useRouter();

  return (
    <div className="flex gap-5">
      <p className="flex gap-2 items-center">
        <EditIcon
          className="w-5 h-5"
          onClick={(e) => {
            e.preventDefault();
            router.push(`/app/products/${productId}/variants/${variantId}`);
          }}
        />
      </p>
      {codesAutogenerated ? (
        <p className="flex gap-2 items-center">
          <LocalOfferIcon
            className="w-5 h-5 text-primary-400 "
            onClick={(e) => {
              e.preventDefault();
              router.push(`/app/products/${productId}/inventory/${variantId}`);
            }}
          />
        </p>
      ) : null}
      <p className="flex gap-2 items-center">
        <MoneyIcon
          className="w-5 h-5 text-primary-400 "
          onClick={(e) => {
            e.preventDefault();
            router.push(
              `/app/products/${productId}/codes?variantId=${variantId}`
            );
          }}
        />
      </p>
    </div>
  );
}

function VariantTable({ variants, product, refetch }: VariantTableProps) {
  const notify = useNotify();

  const [isOpen, setIsOpen] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);

  const [createVariantMutation] = useMutation<{
    createVariant: Variant;
  }>(CREATE_VARIANT);

  const columnHelper = createColumnHelper<Variant>();

  const columns = [
    columnHelper.accessor('title', {
      header: 'Nombre',
      cell: (info) => `${product.title}`,
    }),

    columnHelper.accessor('option', {
      header: product.options[0]?.name ?? '',
      cell: (info) =>
        info.getValue()?.option1 ? `${info.getValue()?.option1}` : '',
    }),

    columnHelper.accessor('option', {
      header: product.options[1]?.name ?? '',
      cell: (info) =>
        product.options[1]?.name ? info.getValue()?.option2 : null,
    }),

    columnHelper.accessor('option', {
      header: product.options[2]?.name ?? '',
      cell: (info) =>
        info.getValue()?.option3 ? `${info.getValue()?.option3}` : null,
    }),

    columnHelper.accessor('price', {
      header: 'Precio',
      cell: (info) =>
        info.getValue() ? `$${formatCurrency(info.getValue())}` : 'N/A',
    }),

    columnHelper.accessor('compareAtPrice', {
      header: 'Precio comparativo',
      cell: (info) =>
        info.getValue() ? `$${formatCurrency(info.getValue())}` : 'N/A',
    }),

    columnHelper.accessor('stock', {
      header: 'Cantidad',
    }),

    columnHelper.accessor('active', {
      header: 'Estatus',
      cell: (info) => <ActiveDotCell active={info?.getValue()} />,
    }),

    columnHelper.display({
      id: 'actions',
      header: 'Acciones',
      cell: (props) => (
        <ActionCell
          productId={product._id}
          variantId={props.row.original._id}
          codesAutogenerated={props.row.original.codesAutogenerated}
        />
      ),
    }),
  ];

  const onClickAddButton = () => {
    setIsOpen(true);
  };

  const createVariant = async (variant: Partial<Variant>) => {
    try {
      NProgress.start();
      if (disabled) return;
      setDisabled(true);
      const { data } = await createVariantMutation({
        variables: {
          data: {
            ...variant,
            productId: product._id,
          },
        },
      });
      if (data) {
        refetch?.();
        notify('Se ha creado la variante', 'success');
      } else {
        notify('Ha ocurrido un error', 'error');
      }
      setIsOpen(false);
    } catch (error) {
      notify(error.message, 'error');
      console.log(error);
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };
  return (
    <>
      <Card className="overflow-x-auto p-5">
        <Table<Variant>
          data={variants ?? []}
          columns={columns}
          href="/app/products"
          manualPagination={false}
        >
          <Table.Header>
            <Table.SearchInput />
            <Table.AddButton title="Variantes" action={onClickAddButton} />
          </Table.Header>
          <Table.Content<Variant> />
          <Table.Footer>
            <Table.Pagination className="ml-auto" />
          </Table.Footer>
        </Table>
      </Card>
      <VariantModal
        isOpen={isOpen}
        setOpen={setIsOpen}
        addVariant={createVariant}
        productOptions={product.options}
        product={product}
      />
    </>
  );
}

export default VariantTable;
